/**
 * Folgeunterlagen aus der Planungsphase — Vertrag der Erzeuger.
 *
 * Ist die Planungsphase erfuellt, legt die App die erforderlichen
 * Folgeunterlagen als ENTWURF an und fuellt sie mit dem, was die Planung
 * gesammelt hat. Hausregel der Planungsautomatik bleibt: sie rechnet und
 * schlaegt vor, gibt aber nie frei, versendet nie, legt nichts in die Ablage
 * und UEBERSCHREIBT NIE einen vorhandenen Datensatz — gibt es die Unterlage
 * schon, bleibt sie, wie sie ist (dort zeigt die Leiste „Aus der
 * Planungsphase" die Vorschlaege wie bisher).
 *
 * Nur Unterlagen mit eigener Tabelle: SiGe-Plan (`sige_projekte`), Handbuch
 * (`handbuecher`), Baustellenordnung (`bauordnungen`), A+S-Plan (`asplaene`).
 * Vorankuendigung und UfsA liegen im Projektbericht (`projects.data`) — dort
 * schreibt die Planungsphase nicht (Sync, Vollblob, Riegel); sie behalten
 * ihre Uebernahme-Leisten.
 */

import type { ReportMetadata } from '../../types';
import type {
    Planungsfrage,
    Planungshinweis,
    Planungsphase,
} from '../../components/planungsphase/types';

export type FolgeArt = 'sigeplan' | 'handbuch' | 'bauordnung' | 'asplan';

/** Reihenfolge des Anlegens: der SiGe-Plan zuerst — das Handbuch leitet seine
 *  Gewerke aus ihm ab. */
export const FOLGE_REIHENFOLGE: ReadonlyArray<FolgeArt> = ['sigeplan', 'handbuch', 'bauordnung', 'asplan'];

/** Alles, was ein Erzeuger aus der Planung lesen darf. Nur GESPEICHERTE
 *  Staende — nie der Entwurf der Maske. */
export interface PlanungsQuelle {
    projectId: string;
    phase: Planungsphase;
    fragen: Planungsfrage[];
    hinweise: Planungshinweis[];
    /** `report.metadata` — Stammdaten, Verteiler, Bauzeit. `null`, wenn der
     *  Aufrufer sie nicht hat; dann bleiben die betroffenen Felder leer. */
    projektDaten: ReportMetadata | null;
    /** Heutiger Tag in Ortszeit, YYYY-MM-DD. */
    heuteIso: string;
    /** Name des Koordinators, der anlegt (Bearbeiter, „aufgestellt von"). */
    bearbeiter: string;
}

/** Was der Erzeuger ueber den Bestand weiss — er LIEST nur. */
export type BestandStand =
    | { status: 'keiner' }
    | { status: 'vorhanden'; beschreibung: string }
    | { status: 'fehler'; fehler: string };

/**
 * Ergebnis des Anlegens. `uebernommen` und `offen` sind ganze Saetze fuer die
 * Ergebnisliste („Baubeschreibung uebernommen", „Arbeitsschritte pruefen").
 * Ein Fehler ist nie still: Teilanlage (Kopf ja, Kapitel nein) meldet
 * `angelegt` mit dem Teil in `offen`.
 */
export type ErzeugungsErgebnis =
    | { status: 'angelegt'; art: FolgeArt; uebernommen: string[]; offen: string[] }
    | { status: 'vorhanden'; art: FolgeArt; beschreibung: string }
    | { status: 'fehler'; art: FolgeArt; fehler: string };

export interface FolgeErzeuger {
    art: FolgeArt;
    /** „SiGe-Plan", „Handbuch der Koordinierung", „Baustellenordnung", „A+S-Plan". */
    titel: string;
    /** Liest, ob es fuer das Projekt schon einen Datensatz gibt. Legt NICHTS an. */
    bestand: (projectId: string) => Promise<BestandStand>;
    /** Legt einen Entwurf an und fuellt ihn aus der Planung. Prueft den Bestand
     *  selbst noch einmal unmittelbar vor dem Schreiben; gibt es ihn inzwischen,
     *  `vorhanden` ohne Aenderung. */
    legeAn: (q: PlanungsQuelle) => Promise<ErzeugungsErgebnis>;
}
